import type { PageQuery } from '#gql'

export const EntryInjectionKey: InjectionKey<Ref<PageQuery['entry'] | null>> =
  Symbol('Entry')

class FetchError extends Error {
  constructor(
    message: string,
    public status: number,
    public cause?: Error
  ) {
    super(message)
  }
}

export const useInjectableEntry = async () => {
  const { locale, locales } = useI18n()
  const route = useRoute()

  const fetchRoute = ref(toValue(route))
  const isInLivePreview = computed(
    () => !!fetchRoute.value.query['live-preview']
  )

  const localeRegex = new RegExp(
    `/(${locales.value.map((l) => (typeof l === 'string' ? l : l.code)).join('|')})(?=/|$)`
  )
  const uri = computed(() => {
    // remove locale prefix from current route path
    let tmp = fetchRoute.value.path.replace(localeRegex, '')
    // remove trailing slash
    tmp = tmp.replace(/\/$/, '')
    // set uri to root if empty
    tmp = tmp === '' ? '/' : tmp

    return tmp
  })

  /**
   * If in live preview, reset GqlHost. This should be done by nuxt-base, but is only done on process.client
   * Should be removed once nuxt-base is updated to do this for us.
   */
  if (route.query.token) {
    // Add query parameters to the preconfigured graphql host
    useGqlHost(`?token=${route.query.token}`)
  }

  const { data, error } = await useAsyncGql('Page', {
    uri: uri.value,
    site: locale.value,
    status: isInLivePreview.value
      ? '(draft|expired|scheduled|published)'
      : 'published',
  })

  if (error.value) {
    useGqlError((err) => {
      // Only log during development
      if (process.env.NODE_ENV === 'production') return

      // throw graphql errors when they exist
      if (err.gqlErrors && err.gqlErrors.length) {
        throw createError({
          statusCode: 500,
          statusMessage: 'GraphQL Error',
          message: err.gqlErrors[0].message,
          data: err.gqlErrors[0],
        })
      }

      throw new FetchError('Could not fetch page (initial)', 500, error.value)
    })
  }

  if (!data.value || !data.value.entry) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Entry not found',
      message: `No entry found with the given uri: "${route.path}"`,
    })
  }

  return computed(() => data.value?.entry as Enforce<PageQuery>['entry'])
}
